import type { ReactNode } from "react";
import { Tooltip } from "./Tooltip.tsx";

/** UI redesign phase 3 finishing touch — extended with `className`/`title`
 * so AppBar.tsx/ToolRail.tsx's own hand-written `<button className="button-
 * icon ...">` elements (the app bar/tool rail's own single-icon actions,
 * 491-line AppToolbar.tsx originally) can use this shared component too,
 * instead of duplicating its markup — `className` layers each caller's own
 * sizing/active-state classes (e.g. "topbar-icon-button", optionally
 * combined with "toolbar-button-active") on top of the shared "button-icon"/
 * "button-icon-danger" base, same composition every existing caller
 * (LidControlsEditor.tsx/NamedCollectionHeader.tsx/UnitHydrographsEditor.tsx)
 * already relies on implicitly.
 *
 * Wrapped in `Tooltip` (audit finding on native `title` being ~1 s):
 * every AppBar/ToolRail icon-only button goes through this component, so
 * plugging the custom tooltip in here — not into each callsite —
 * automatically covers the sites the audit named. The native `title`
 * attribute is intentionally NOT set on the underlying button: leaving it
 * would show a second (browser-native) tooltip a beat later next to the
 * custom one. `aria-label` stays the accessible name in every case. */
export function IconButton({
  icon,
  label,
  onClick,
  danger,
  disabled,
  className,
  title,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Extra class(es) appended after the shared "button-icon"/"button-icon-danger" base. */
  className?: string;
  /** Defaults to `label` — set separately only when the tooltip should differ from the accessible name (mirrors DropdownMenu.tsx's own `triggerTitle`). */
  title?: string;
}) {
  const classes = ["button-icon", danger && "button-icon-danger", className]
    .filter(Boolean)
    .join(" ");
  return (
    <Tooltip label={title ?? label}>
      <button className={classes} onClick={onClick} disabled={disabled} aria-label={label}>
        {icon}
      </button>
    </Tooltip>
  );
}
